import { hashKey, QueryObserver } from "@tanstack/query-core";
import { deepEquals } from "@tanstack/db";
import { QueryKeyRequiredError, QueryFnRequiredError, QueryClientRequiredError, GetKeyRequiredError } from "./errors.js";
import { createWriteUtils } from "./manual-sync.js";
import { serializeLoadSubsetOptions } from "./serialization.js";
const QUERY_COLLECTION_GC_PREFIX = `queryCollection:gc:`;
class QueryCollectionUtilsImpl {
  constructor(state, refetch, writeUtils) {
    this.state = state;
    this.refetchFn = refetch;
    this.refetch = refetch;
    this.writeInsert = writeUtils.writeInsert;
    this.writeUpdate = writeUtils.writeUpdate;
    this.writeDelete = writeUtils.writeDelete;
    this.writeUpsert = writeUtils.writeUpsert;
    this.writeBatch = writeUtils.writeBatch;
  }
  async clearError() {
    this.state.lastError = void 0;
    this.state.errorCount = 0;
    this.state.lastErrorUpdatedAt = 0;
    await this.refetchFn({ throwOnError: true });
  }
  // Getters for error state
  get lastError() {
    return this.state.lastError;
  }
  get isError() {
    return !!this.state.lastError;
  }
  get errorCount() {
    return this.state.errorCount;
  }
  // Getters for QueryObserver state
  get isFetching() {
    return Array.from(this.state.observers.values()).some(
      (observer) => observer.getCurrentResult().isFetching
    );
  }
  get isRefetching() {
    return Array.from(this.state.observers.values()).some(
      (observer) => observer.getCurrentResult().isRefetching
    );
  }
  get isLoading() {
    return Array.from(this.state.observers.values()).some(
      (observer) => observer.getCurrentResult().isLoading
    );
  }
  get dataUpdatedAt() {
    return Math.max(
      0,
      ...Array.from(this.state.observers.values()).map(
        (observer) => observer.getCurrentResult().dataUpdatedAt
      )
    );
  }
  get fetchStatus() {
    return Array.from(this.state.observers.values()).map(
      (observer) => observer.getCurrentResult().fetchStatus
    );
  }
}
function queryCollectionOptions(config) {
  const {
    queryKey,
    queryFn,
    select,
    queryClient,
    enabled,
    refetchInterval,
    retry,
    retryDelay,
    staleTime,
    persistedGcTime,
    getKey,
    onInsert,
    onUpdate,
    onDelete,
    meta,
    ...baseCollectionConfig
  } = config;
  const syncMode = baseCollectionConfig.syncMode ?? `eager`;
  const baseKey = typeof queryKey === `function` ? queryKey({}) : queryKey;
  const validateQueryKeyPrefix = (key) => {
    if (typeof queryKey !== `function`) return;
    const isValidPrefix = key.length >= baseKey.length && baseKey.every((segment, i) => deepEquals(segment, key[i]));
    if (!isValidPrefix) {
      console.warn(
        `[QueryCollection] queryKey function must return keys that extend the base key prefix. Base: ${JSON.stringify(baseKey)}, Got: ${JSON.stringify(key)}. This can cause stale cache issues.`
      );
    }
  };
  if (!queryKey) {
    throw new QueryKeyRequiredError();
  }
  if (!queryFn) {
    throw new QueryFnRequiredError();
  }
  if (!queryClient) {
    throw new QueryClientRequiredError();
  }
  if (!getKey) {
    throw new GetKeyRequiredError();
  }
  const state = {
    lastError: void 0,
    errorCount: 0,
    lastErrorUpdatedAt: 0,
    observers: /* @__PURE__ */ new Map()
  };
  const hashToQueryKey = /* @__PURE__ */ new Map();
  const queryToRows = /* @__PURE__ */ new Map();
  const rowToQueries = /* @__PURE__ */ new Map();
  const unsubscribes = /* @__PURE__ */ new Map();
  const queryRefCounts = /* @__PURE__ */ new Map();
  const addRow = (rowKey, hashedQueryKey) => {
    const rowToQueriesSet = rowToQueries.get(rowKey) || /* @__PURE__ */ new Set();
    rowToQueriesSet.add(hashedQueryKey);
    rowToQueries.set(rowKey, rowToQueriesSet);
    const queryToRowsSet = queryToRows.get(hashedQueryKey) || /* @__PURE__ */ new Set();
    queryToRowsSet.add(rowKey);
    queryToRows.set(hashedQueryKey, queryToRowsSet);
  };
  const removeRow = (rowKey, hashedQuerKey) => {
    const rowToQueriesSet = rowToQueries.get(rowKey) || /* @__PURE__ */ new Set();
    rowToQueriesSet.delete(hashedQuerKey);
    rowToQueries.set(rowKey, rowToQueriesSet);
    const queryToRowsSet = queryToRows.get(hashedQuerKey) || /* @__PURE__ */ new Set();
    queryToRowsSet.delete(rowKey);
    queryToRows.set(hashedQuerKey, queryToRowsSet);
    return rowToQueriesSet.size === 0;
  };
  const internalSync = (params) => {
    const { begin, write, commit, markReady, collection, metadata } = params;
    const persistedMetadata = metadata;
    let syncStarted = false;
    let startupRetentionSettled = false;
    const retainedQueriesPendingRevalidation = /* @__PURE__ */ new Set();
    const effectivePersistedGcTimes = /* @__PURE__ */ new Map();
    const persistedRetentionTimers = /* @__PURE__ */ new Map();
    let persistedRetentionMaintenance = Promise.resolve();
    const getRowMetadata = (rowKey) => {
      return metadata?.row.get(rowKey) ?? collection._state.syncedMetadata.get(rowKey);
    };
    const getPersistedOwners = (rowKey) => {
      const rowMetadata = getRowMetadata(rowKey);
      const queryMetadata = rowMetadata?.queryCollection;
      if (!queryMetadata || typeof queryMetadata !== `object`) {
        return /* @__PURE__ */ new Set();
      }
      const owners = queryMetadata.owners;
      if (!owners || typeof owners !== `object`) {
        return /* @__PURE__ */ new Set();
      }
      return new Set(Object.keys(owners));
    };
    const setPersistedOwners = (rowKey, owners) => {
      if (!metadata) {
        return;
      }
      const currentMetadata = { ...getRowMetadata(rowKey) ?? {} };
      if (owners.size === 0) {
        delete currentMetadata.queryCollection;
        if (Object.keys(currentMetadata).length === 0) {
          metadata.row.delete(rowKey);
        } else {
          metadata.row.set(rowKey, currentMetadata);
        }
        return;
      }
      metadata.row.set(rowKey, {
        ...currentMetadata,
        queryCollection: {
          owners: Object.fromEntries(
            Array.from(owners.values()).map((owner) => [owner, true])
          )
        }
      });
    };
    const parsePersistedQueryRetentionEntry = (value, expectedHash) => {
      if (!value || typeof value !== `object`) {
        return void 0;
      }
      const record = value;
      if (record.queryHash !== expectedHash) {
        return void 0;
      }
      if (record.mode === `until-revalidated`) {
        return {
          queryHash: expectedHash,
          mode: `until-revalidated`
        };
      }
      if (record.mode === `ttl` && typeof record.expiresAt === `number` && Number.isFinite(record.expiresAt)) {
        return {
          queryHash: expectedHash,
          mode: `ttl`,
          expiresAt: record.expiresAt
        };
      }
      return void 0;
    };
    const runPersistedRetentionMaintenance = (task) => {
      persistedRetentionMaintenance = persistedRetentionMaintenance.then(
        task,
        task
      );
      return persistedRetentionMaintenance;
    };
    const cancelPersistedRetentionExpiry = (hashedQueryKey) => {
      const timer = persistedRetentionTimers.get(hashedQueryKey);
      if (timer) {
        clearTimeout(timer);
        persistedRetentionTimers.delete(hashedQueryKey);
      }
    };
    const getHydratedOwnedRowsForQueryBaseline = (hashedQueryKey) => {
      const knownRows = queryToRows.get(hashedQueryKey);
      if (knownRows) {
        return new Set(knownRows);
      }
      const ownedRows = /* @__PURE__ */ new Set();
      for (const [rowKey] of collection._state.syncedData.entries()) {
        const owners = getPersistedOwners(rowKey);
        if (owners.size === 0) {
          continue;
        }
        rowToQueries.set(rowKey, new Set(owners));
        owners.forEach((owner) => {
          const queryToRowsSet = queryToRows.get(owner) || /* @__PURE__ */ new Set();
          queryToRowsSet.add(rowKey);
          queryToRows.set(owner, queryToRowsSet);
        });
        if (owners.has(hashedQueryKey)) {
          ownedRows.add(rowKey);
        }
      }
      return ownedRows;
    };
    const loadPersistedBaselineForQuery = async (hashedQueryKey) => {
      const knownRows = queryToRows.get(hashedQueryKey);
      if (knownRows && Array.from(knownRows).every((rowKey) => collection.has(rowKey))) {
        const baseline2 = /* @__PURE__ */ new Map();
        knownRows.forEach((rowKey) => {
          const value = collection.get(rowKey);
          const owners = rowToQueries.get(rowKey);
          if (value && owners) {
            baseline2.set(rowKey, {
              value,
              owners: new Set(owners)
            });
          }
        });
        return baseline2;
      }
      const scanPersisted = persistedMetadata?.row.scanPersisted;
      if (!scanPersisted) {
        const baseline2 = /* @__PURE__ */ new Map();
        getHydratedOwnedRowsForQueryBaseline(hashedQueryKey).forEach(
          (rowKey) => {
            const value = collection.get(rowKey);
            const owners = rowToQueries.get(rowKey);
            if (value && owners) {
              baseline2.set(rowKey, {
                value,
                owners: new Set(owners)
              });
            }
          }
        );
        return baseline2;
      }
      const baseline = /* @__PURE__ */ new Map();
      const scannedRows = await scanPersisted();
      scannedRows.forEach((row) => {
        const rowMetadata = row.metadata;
        const queryMetadata = rowMetadata?.queryCollection;
        if (!queryMetadata || typeof queryMetadata !== `object`) {
          return;
        }
        const owners = queryMetadata.owners;
        if (!owners || typeof owners !== `object`) {
          return;
        }
        const ownerSet = new Set(Object.keys(owners));
        if (ownerSet.size === 0) {
          return;
        }
        rowToQueries.set(row.key, new Set(ownerSet));
        ownerSet.forEach((owner) => {
          const queryToRowsSet = queryToRows.get(owner) || /* @__PURE__ */ new Set();
          queryToRowsSet.add(row.key);
          queryToRows.set(owner, queryToRowsSet);
        });
        if (ownerSet.has(hashedQueryKey)) {
          baseline.set(row.key, {
            value: row.value,
            owners: ownerSet
          });
        }
      });
      return baseline;
    };
    const cleanupPersistedPlaceholder = async (hashedQueryKey) => {
      if (!metadata) {
        return;
      }
      const baseline = await loadPersistedBaselineForQuery(hashedQueryKey);
      const rowsToDelete = [];
      begin();
      baseline.forEach(({ value: oldItem, owners }, rowKey) => {
        owners.delete(hashedQueryKey);
        setPersistedOwners(rowKey, owners);
        const needToRemove = removeRow(rowKey, hashedQueryKey);
        if (needToRemove) {
          rowsToDelete.push(oldItem);
        }
      });
      rowsToDelete.forEach((row) => {
        write({ type: `delete`, value: row });
      });
      metadata.collection.delete(
        `${QUERY_COLLECTION_GC_PREFIX}${hashedQueryKey}`
      );
      commit();
    };
    const schedulePersistedRetentionExpiry = (entry) => {
      if (entry.mode !== `ttl`) {
        return;
      }
      cancelPersistedRetentionExpiry(entry.queryHash);
      const delay = Math.max(0, entry.expiresAt - Date.now());
      const timer = setTimeout(() => {
        persistedRetentionTimers.delete(entry.queryHash);
        void runPersistedRetentionMaintenance(async () => {
          const currentEntry = metadata?.collection.get(
            `${QUERY_COLLECTION_GC_PREFIX}${entry.queryHash}`
          );
          const parsedCurrentEntry = parsePersistedQueryRetentionEntry(
            currentEntry,
            entry.queryHash
          );
          if (!parsedCurrentEntry || parsedCurrentEntry.mode !== `ttl` || parsedCurrentEntry.expiresAt > Date.now()) {
            return;
          }
          await cleanupPersistedPlaceholder(entry.queryHash);
        });
      }, delay);
      persistedRetentionTimers.set(entry.queryHash, timer);
    };
    const consumePersistedQueryRetentionAtStartup = async () => {
      if (!metadata) {
        return;
      }
      const retentionEntries = metadata.collection.list(
        QUERY_COLLECTION_GC_PREFIX
      );
      const now = Date.now();
      for (const { key, value } of retentionEntries) {
        const hashedQueryKey = key.slice(QUERY_COLLECTION_GC_PREFIX.length);
        const parsed = parsePersistedQueryRetentionEntry(value, hashedQueryKey);
        if (!parsed) {
          continue;
        }
        if (parsed.mode === `ttl` && parsed.expiresAt <= now) {
          await cleanupPersistedPlaceholder(parsed.queryHash);
        } else if (parsed.mode === `ttl`) {
          schedulePersistedRetentionExpiry(parsed);
        }
      }
    };
    const generateQueryKeyFromOptions = (opts) => {
      if (typeof queryKey === `function`) {
        return queryKey(opts);
      } else if (syncMode === `on-demand`) {
        const serialized = serializeLoadSubsetOptions(opts);
        return serialized !== void 0 ? [...queryKey, serialized] : queryKey;
      } else {
        return queryKey;
      }
    };
    const startupRetentionEntries = metadata?.collection.list(
      QUERY_COLLECTION_GC_PREFIX
    );
    const startupRetentionMaintenancePromise = !startupRetentionEntries || startupRetentionEntries.length === 0 ? (() => {
      startupRetentionSettled = true;
      return Promise.resolve();
    })() : runPersistedRetentionMaintenance(async () => {
      try {
        await consumePersistedQueryRetentionAtStartup();
      } finally {
        startupRetentionSettled = true;
      }
    });
    const createQueryFromOpts = (opts = {}, queryFunction = queryFn) => {
      if (!startupRetentionSettled) {
        return startupRetentionMaintenancePromise.then(() => {
          const resumed = createQueryFromOpts(opts, queryFunction);
          return resumed === true ? void 0 : resumed;
        });
      }
      const key = generateQueryKeyFromOptions(opts);
      const hashedQueryKey = hashKey(key);
      const extendedMeta = { ...meta, loadSubsetOptions: opts };
      const retainedEntry = metadata?.collection.get(
        `${QUERY_COLLECTION_GC_PREFIX}${hashedQueryKey}`
      );
      if (parsePersistedQueryRetentionEntry(retainedEntry, hashedQueryKey) !== void 0) {
        retainedQueriesPendingRevalidation.add(hashedQueryKey);
      }
      cancelPersistedRetentionExpiry(hashedQueryKey);
      validateQueryKeyPrefix(key);
      if (state.observers.has(hashedQueryKey)) {
        queryRefCounts.set(
          hashedQueryKey,
          (queryRefCounts.get(hashedQueryKey) || 0) + 1
        );
        const observer = state.observers.get(hashedQueryKey);
        const currentResult = observer.getCurrentResult();
        if (currentResult.isSuccess) {
          return true;
        } else if (currentResult.isError) {
          return Promise.reject(currentResult.error);
        } else {
          const cachedData2 = queryClient.getQueryData(key);
          if (cachedData2 !== void 0) {
            return true;
          }
          return new Promise((resolve, reject) => {
            const unsubscribe = observer.subscribe((result) => {
              queueMicrotask(() => {
                if (result.isSuccess) {
                  unsubscribe();
                  resolve();
                } else if (result.isError) {
                  unsubscribe();
                  reject(result.error);
                }
              });
            });
          });
        }
      }
      const observerOptions = {
        queryKey: key,
        queryFn: queryFunction,
        meta: extendedMeta,
        structuralSharing: true,
        notifyOnChangeProps: `all`,
        // Only include options that are explicitly defined to allow QueryClient defaultOptions to be used
        ...enabled !== void 0 && { enabled },
        ...refetchInterval !== void 0 && { refetchInterval },
        ...retry !== void 0 && { retry },
        ...retryDelay !== void 0 && { retryDelay },
        ...staleTime !== void 0 && { staleTime }
      };
      const localObserver = new QueryObserver(queryClient, observerOptions);
      const resolvedQueryGcTime = queryClient.getQueryCache().find({
        queryKey: key,
        exact: true
      })?.gcTime;
      const effectivePersistedGcTime = persistedGcTime ?? resolvedQueryGcTime;
      hashToQueryKey.set(hashedQueryKey, key);
      state.observers.set(hashedQueryKey, localObserver);
      if (effectivePersistedGcTime !== void 0) {
        effectivePersistedGcTimes.set(hashedQueryKey, effectivePersistedGcTime);
      } else {
        effectivePersistedGcTimes.delete(hashedQueryKey);
      }
      queryRefCounts.set(
        hashedQueryKey,
        (queryRefCounts.get(hashedQueryKey) || 0) + 1
      );
      const cachedData = queryClient.getQueryData(key);
      if (cachedData !== void 0) {
        if (syncStarted || collection.subscriberCount > 0) {
          subscribeToQuery(localObserver, hashedQueryKey);
        }
        return true;
      }
      const readyPromise = new Promise((resolve, reject) => {
        const unsubscribe = localObserver.subscribe((result) => {
          queueMicrotask(() => {
            if (result.isSuccess) {
              unsubscribe();
              resolve();
            } else if (result.isError) {
              unsubscribe();
              reject(result.error);
            }
          });
        });
      });
      if (syncStarted || collection.subscriberCount > 0) {
        subscribeToQuery(localObserver, hashedQueryKey);
      }
      return readyPromise;
    };
    const applySuccessfulResult = (queryKey2, result, persistedBaseline) => {
      const hashedQueryKey = hashKey(queryKey2);
      if (collection.status === `cleaned-up`) {
        return;
      }
      state.lastError = void 0;
      state.errorCount = 0;
      const rawData = result.data;
      const newItemsArray = select ? select(rawData) : rawData;
      if (!Array.isArray(newItemsArray) || newItemsArray.some((item) => typeof item !== `object`)) {
        const errorMessage = select ? `@tanstack/query-db-collection: select() must return an array of objects. Got: ${typeof newItemsArray} for queryKey ${JSON.stringify(queryKey2)}` : `@tanstack/query-db-collection: queryFn must return an array of objects. Got: ${typeof newItemsArray} for queryKey ${JSON.stringify(queryKey2)}`;
        console.error(errorMessage);
        return;
      }
      const currentSyncedItems = new Map(
        collection._state.syncedData.entries()
      );
      const shouldUsePersistedBaseline = persistedBaseline !== void 0;
      const previouslyOwnedRows = shouldUsePersistedBaseline ? new Set(persistedBaseline.keys()) : getHydratedOwnedRowsForQueryBaseline(hashedQueryKey);
      const newItemsMap = /* @__PURE__ */ new Map();
      newItemsArray.forEach((item) => {
        const key = getKey(item);
        newItemsMap.set(key, item);
      });
      begin();
      if (metadata) {
        metadata.collection.delete(
          `${QUERY_COLLECTION_GC_PREFIX}${hashedQueryKey}`
        );
      }
      previouslyOwnedRows.forEach((key) => {
        const oldItem = shouldUsePersistedBaseline ? persistedBaseline.get(key)?.value : currentSyncedItems.get(key);
        if (!oldItem) {
          return;
        }
        const newItem = newItemsMap.get(key);
        if (!newItem) {
          const owners = getPersistedOwners(key);
          owners.delete(hashedQueryKey);
          setPersistedOwners(key, owners);
          const needToRemove = removeRow(key, hashedQueryKey);
          if (needToRemove) {
            write({ type: `delete`, value: oldItem });
          }
        } else if (!deepEquals(oldItem, newItem)) {
          write({ type: `update`, value: newItem });
        }
      });
      newItemsMap.forEach((newItem, key) => {
        const owners = getPersistedOwners(key);
        if (!owners.has(hashedQueryKey)) {
          owners.add(hashedQueryKey);
          setPersistedOwners(key, owners);
        }
        addRow(key, hashedQueryKey);
        if (!currentSyncedItems.has(key)) {
          write({ type: `insert`, value: newItem });
        }
      });
      commit();
      retainedQueriesPendingRevalidation.delete(hashedQueryKey);
      cancelPersistedRetentionExpiry(hashedQueryKey);
      markReady();
    };
    const reconcileSuccessfulResult = async (queryKey2, result) => {
      const hashedQueryKey = hashKey(queryKey2);
      const persistedBaseline = await loadPersistedBaselineForQuery(hashedQueryKey);
      if (collection.status === `cleaned-up`) {
        return;
      }
      applySuccessfulResult(queryKey2, result, persistedBaseline);
    };
    const makeQueryResultHandler = (queryKey2) => {
      const hashedQueryKey = hashKey(queryKey2);
      const handleQueryResult = (result) => {
        if (result.isSuccess) {
          if (collection.deferDataRefresh) {
            collection.deferDataRefresh.then(() => {
              const observer = state.observers.get(hashedQueryKey);
              if (observer) {
                observer.refetch().catch(() => {
                });
              }
            });
            return;
          }
          if (retainedQueriesPendingRevalidation.has(hashedQueryKey)) {
            void reconcileSuccessfulResult(queryKey2, result).catch((error) => {
              console.error(
                `[QueryCollection] Error reconciling query ${String(queryKey2)}:`,
                error
              );
            });
          } else {
            applySuccessfulResult(queryKey2, result);
          }
        } else if (result.isError) {
          const isNewError = result.errorUpdatedAt !== state.lastErrorUpdatedAt || result.error !== state.lastError;
          if (isNewError) {
            state.lastError = result.error;
            state.errorCount++;
            state.lastErrorUpdatedAt = result.errorUpdatedAt;
          }
          console.error(
            `[QueryCollection] Error observing query ${String(queryKey2)}:`,
            result.error
          );
          markReady();
        }
      };
      return handleQueryResult;
    };
    const isSubscribed = (hashedQueryKey) => {
      return unsubscribes.has(hashedQueryKey);
    };
    const subscribeToQuery = (observer, hashedQueryKey) => {
      if (!isSubscribed(hashedQueryKey)) {
        const cachedQueryKey = hashToQueryKey.get(hashedQueryKey);
        const handleQueryResult = makeQueryResultHandler(cachedQueryKey);
        const unsubscribeFn = observer.subscribe(handleQueryResult);
        unsubscribes.set(hashedQueryKey, unsubscribeFn);
        const currentResult = observer.getCurrentResult();
        if (currentResult.isSuccess || currentResult.isError) {
          handleQueryResult(currentResult);
        }
      }
    };
    const subscribeToQueries = () => {
      state.observers.forEach(subscribeToQuery);
    };
    const unsubscribeFromQueries = () => {
      unsubscribes.forEach((unsubscribeFn) => {
        unsubscribeFn();
      });
      unsubscribes.clear();
    };
    syncStarted = true;
    const unsubscribeFromCollectionEvents = collection.on(
      `subscribers:change`,
      ({ subscriberCount }) => {
        if (subscriberCount > 0) {
          subscribeToQueries();
        } else if (subscriberCount === 0) {
          unsubscribeFromQueries();
        }
      }
    );
    if (syncMode === `eager`) {
      const initialResult = createQueryFromOpts({});
      if (initialResult instanceof Promise) {
        initialResult.catch(() => {
        });
      }
    } else {
      if (startupRetentionSettled) {
        markReady();
      } else {
        void startupRetentionMaintenancePromise.then(() => {
          markReady();
        });
      }
    }
    subscribeToQueries();
    state.observers.forEach((observer, hashedQueryKey) => {
      const cachedQueryKey = hashToQueryKey.get(hashedQueryKey);
      const handleQueryResult = makeQueryResultHandler(cachedQueryKey);
      handleQueryResult(observer.getCurrentResult());
    });
    const cleanupQueryInternal = (hashedQueryKey) => {
      unsubscribes.get(hashedQueryKey)?.();
      unsubscribes.delete(hashedQueryKey);
      cancelPersistedRetentionExpiry(hashedQueryKey);
      retainedQueriesPendingRevalidation.delete(hashedQueryKey);
      const rowKeys = queryToRows.get(hashedQueryKey) ?? /* @__PURE__ */ new Set();
      const nextOwnersByRow = /* @__PURE__ */ new Map();
      const rowsToDelete = [];
      rowKeys.forEach((rowKey) => {
        const queries = rowToQueries.get(rowKey);
        if (!queries) {
          return;
        }
        const nextOwners = new Set(queries);
        nextOwners.delete(hashedQueryKey);
        nextOwnersByRow.set(rowKey, nextOwners);
        if (nextOwners.size === 0 && collection.has(rowKey)) {
          rowsToDelete.push(collection.get(rowKey));
        }
      });
      const shouldWriteMetadata = metadata !== void 0 && nextOwnersByRow.size > 0;
      const needsTransaction = shouldWriteMetadata || rowsToDelete.length > 0;
      if (needsTransaction) {
        begin();
      }
      nextOwnersByRow.forEach((owners, rowKey) => {
        if (owners.size === 0) {
          rowToQueries.delete(rowKey);
        } else {
          rowToQueries.set(rowKey, owners);
        }
        if (shouldWriteMetadata) {
          setPersistedOwners(rowKey, owners);
        }
      });
      if (rowsToDelete.length > 0) {
        rowsToDelete.forEach((row) => {
          write({ type: `delete`, value: row });
        });
      }
      if (needsTransaction) {
        commit();
      }
      state.observers.delete(hashedQueryKey);
      queryToRows.delete(hashedQueryKey);
      hashToQueryKey.delete(hashedQueryKey);
      queryRefCounts.delete(hashedQueryKey);
      effectivePersistedGcTimes.delete(hashedQueryKey);
    };
    const cleanupQueryIfIdle = (hashedQueryKey) => {
      const refcount = queryRefCounts.get(hashedQueryKey) || 0;
      const observer = state.observers.get(hashedQueryKey);
      const effectivePersistedGcTime = effectivePersistedGcTimes.get(hashedQueryKey);
      if (refcount <= 0) {
        unsubscribes.get(hashedQueryKey)?.();
        unsubscribes.delete(hashedQueryKey);
      }
      const hasListeners = observer?.hasListeners() ?? false;
      if (hasListeners) {
        queryRefCounts.set(hashedQueryKey, 0);
        return;
      }
      if (refcount > 0) {
        console.warn(
          `[cleanupQueryIfIdle] Invariant violation: refcount=${refcount} but no listeners. Cleaning up to prevent leak.`,
          { hashedQueryKey }
        );
      }
      if (effectivePersistedGcTime !== void 0 && metadata && persistedMetadata?.row.scanPersisted) {
        begin();
        metadata.collection.set(
          `${QUERY_COLLECTION_GC_PREFIX}${hashedQueryKey}`,
          {
            queryHash: hashedQueryKey,
            mode: effectivePersistedGcTime === Number.POSITIVE_INFINITY ? `until-revalidated` : `ttl`,
            ...effectivePersistedGcTime === Number.POSITIVE_INFINITY ? {} : { expiresAt: Date.now() + effectivePersistedGcTime }
          }
        );
        commit();
        if (effectivePersistedGcTime !== Number.POSITIVE_INFINITY) {
          schedulePersistedRetentionExpiry({
            queryHash: hashedQueryKey,
            mode: `ttl`,
            expiresAt: Date.now() + effectivePersistedGcTime
          });
        }
        unsubscribes.get(hashedQueryKey)?.();
        unsubscribes.delete(hashedQueryKey);
        state.observers.delete(hashedQueryKey);
        hashToQueryKey.delete(hashedQueryKey);
        queryRefCounts.set(hashedQueryKey, 0);
        return;
      }
      cleanupQueryInternal(hashedQueryKey);
    };
    const forceCleanupQuery = (hashedQueryKey) => {
      cleanupQueryInternal(hashedQueryKey);
    };
    const unsubscribeQueryCache = queryClient.getQueryCache().subscribe((event) => {
      const hashedKey = event.query.queryHash;
      if (event.type === `removed`) {
        if (hashToQueryKey.has(hashedKey)) {
          cleanupQueryIfIdle(hashedKey);
        }
      }
    });
    const cleanup = async () => {
      unsubscribeFromCollectionEvents();
      unsubscribeFromQueries();
      persistedRetentionTimers.forEach((timer) => {
        clearTimeout(timer);
      });
      persistedRetentionTimers.clear();
      const allQueryKeys = [...hashToQueryKey.values()];
      const allHashedKeys = [...state.observers.keys()];
      for (const hashedKey of allHashedKeys) {
        forceCleanupQuery(hashedKey);
      }
      unsubscribeQueryCache();
      await Promise.all(
        allQueryKeys.map(async (qKey) => {
          await queryClient.cancelQueries({ queryKey: qKey, exact: true });
          queryClient.removeQueries({ queryKey: qKey, exact: true });
        })
      );
    };
    const unloadSubset = (options) => {
      const key = generateQueryKeyFromOptions(options);
      const hashedQueryKey = hashKey(key);
      const currentCount = queryRefCounts.get(hashedQueryKey) || 0;
      const newCount = currentCount - 1;
      if (newCount <= 0) {
        queryRefCounts.set(hashedQueryKey, 0);
        cleanupQueryIfIdle(hashedQueryKey);
      } else {
        queryRefCounts.set(hashedQueryKey, newCount);
      }
    };
    const loadSubsetDedupe = syncMode === `eager` ? void 0 : createQueryFromOpts;
    return {
      loadSubset: loadSubsetDedupe,
      unloadSubset: syncMode === `eager` ? void 0 : unloadSubset,
      cleanup
    };
  };
  const refetch = async (opts) => {
    const allQueryKeys = [...hashToQueryKey.values()];
    const refetchPromises = allQueryKeys.map((qKey) => {
      const queryObserver = state.observers.get(hashKey(qKey));
      return queryObserver.refetch({
        throwOnError: opts?.throwOnError
      });
    });
    return Promise.all(refetchPromises);
  };
  const updateCacheDataForKey = (key, items) => {
    if (select) {
      queryClient.setQueryData(key, (oldData) => {
        if (!oldData || typeof oldData !== `object`) {
          return oldData;
        }
        if (Array.isArray(oldData)) {
          return items;
        }
        const selectedArray = select(oldData);
        if (Array.isArray(selectedArray)) {
          for (const propKey of Object.keys(oldData)) {
            if (oldData[propKey] === selectedArray) {
              return { ...oldData, [propKey]: items };
            }
          }
        }
        if (Array.isArray(oldData.data)) {
          return { ...oldData, data: items };
        }
        if (Array.isArray(oldData.items)) {
          return { ...oldData, items };
        }
        if (Array.isArray(oldData.results)) {
          return { ...oldData, results: items };
        }
        for (const propKey of Object.keys(oldData)) {
          if (Array.isArray(oldData[propKey])) {
            return { ...oldData, [propKey]: items };
          }
        }
        return oldData;
      });
    } else {
      queryClient.setQueryData(key, items);
    }
  };
  const updateCacheData = (items) => {
    const allCached = queryClient.getQueryCache().findAll({ queryKey: baseKey });
    if (allCached.length > 0) {
      for (const query of allCached) {
        updateCacheDataForKey(query.queryKey, items);
      }
    } else {
      updateCacheDataForKey(baseKey, items);
    }
  };
  let writeContext = null;
  const enhancedInternalSync = (params) => {
    const { begin, write, commit, collection } = params;
    const contextQueryKey = typeof queryKey === `function` ? queryKey({}) : queryKey;
    writeContext = {
      collection,
      queryClient,
      queryKey: contextQueryKey,
      getKey,
      begin,
      write,
      commit,
      updateCacheData
    };
    return internalSync(params);
  };
  const writeUtils = createWriteUtils(
    () => writeContext
  );
  const wrappedOnInsert = onInsert ? async (params) => {
    const handlerResult = await onInsert(params) ?? {};
    const shouldRefetch = handlerResult.refetch !== false;
    if (shouldRefetch) {
      await refetch();
    }
    return handlerResult;
  } : void 0;
  const wrappedOnUpdate = onUpdate ? async (params) => {
    const handlerResult = await onUpdate(params) ?? {};
    const shouldRefetch = handlerResult.refetch !== false;
    if (shouldRefetch) {
      await refetch();
    }
    return handlerResult;
  } : void 0;
  const wrappedOnDelete = onDelete ? async (params) => {
    const handlerResult = await onDelete(params) ?? {};
    const shouldRefetch = handlerResult.refetch !== false;
    if (shouldRefetch) {
      await refetch();
    }
    return handlerResult;
  } : void 0;
  const utils = new QueryCollectionUtilsImpl(state, refetch, writeUtils);
  return {
    ...baseCollectionConfig,
    getKey,
    syncMode,
    sync: { sync: enhancedInternalSync },
    onInsert: wrappedOnInsert,
    onUpdate: wrappedOnUpdate,
    onDelete: wrappedOnDelete,
    utils
  };
}
export {
  queryCollectionOptions
};
//# sourceMappingURL=query.js.map
